import React, { useEffect, useRef, useState } from 'react';
import { Video, Image as ImageIcon, FileText, Play } from 'lucide-react';
import { contentStrategy } from '../data/mock';

const ContentStrategy = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const icons = [Video, ImageIcon, FileText, Play];

  return (
    <section ref={sectionRef} className="py-24 bg-[#0B0B0B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text Content */}
          <div className="space-y-8">
            <div>
              <h2 
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'all 0.8s ease-out'
                }}
              >
                {contentStrategy.headline}
              </h2>
              <p 
                className="text-lg text-white/70"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'all 0.8s ease-out 0.2s'
                }}
              >
                {contentStrategy.description}
              </p>
            </div>

            {/* Content Formats */}
            <div className="space-y-4">
              {contentStrategy.formats.map((format, index) => {
                const Icon = icons[index];
                return (
                  <div
                    key={index}
                    className="flex items-start space-x-4 p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg hover:bg-white/10 transition-all duration-300"
                    style={{
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'translateX(0)' : 'translateX(-30px)',
                      transition: `all 0.6s ease-out ${0.3 + index * 0.1}s`
                    }}
                  >
                    <div 
                      className="flex-shrink-0"
                      style={{
                        transform: isVisible ? 'scale(1)' : 'scale(0)',
                        transition: `transform 0.4s ease-out ${0.4 + index * 0.1}s`
                      }}
                    >
                      <Icon className="text-[#2563EB]" size={24} />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{format.title}</h3>
                      <p className="text-white/60 text-sm">{format.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Image */}
          <div 
            className="relative"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1)' : 'scale(0.9)',
              transition: 'all 0.8s ease-out 0.4s'
            }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src={contentStrategy.image}
                alt="Content Strategy"
                className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2563EB]/30 to-transparent" />
            </div>
            {/* Decorative blur */}
            <div className="absolute -z-10 -bottom-10 left-1/2 -translate-x-1/2 w-3/4 h-40 bg-[#2563EB]/20 rounded-full blur-3xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContentStrategy;
